import { useState, useRef, useEffect, useCallback, MutableRefObject } from 'react';
import { usePreviousValue } from '../../hooks/usePreviousValue';
import { TreeNode } from './Tree.heplers';
import { DataNode, ITreeNodeValue, Key } from './Tree.interfaces';
const useTree = props => {
  const {
    treeData,
    defaultExpandedKeys = [],
    defaultCheckedKeys = [],
    checkable
  } = props;
  const rootRef = useRef(null);
  const prevProps = usePreviousValue({
    ...props,
    defaultExpandedKeys,
    defaultCheckedKeys
  });
  const [checkedKeys, setCheckedKeys] = useState(defaultCheckedKeys);
  const [expandedKeys, setExpandedKeys] = useState(defaultExpandedKeys);
  const [list, setList] = useState([]);
  const preOrderTraversal = useCallback(function* (node = rootRef.current) {
    yield node;
    if (node.children.length) {
      for (let child of node.children) {
        yield* preOrderTraversal(child);
      }
    }
  }, [rootRef]);
  const postOrderTraversal = useCallback(function* (node = rootRef.current) {
    if (node.children.length) {
      for (let child of node.children) {
        yield* postOrderTraversal(child);
      }
    }
    yield node;
  }, [rootRef]);
  const insertNode = useCallback((parentNodeKey, key, value) => {
    for (let node of preOrderTraversal()) {
      if (node.key === parentNodeKey) {
        node.children.push(new TreeNode(key, value, node));
        node.expanded = expandedKeys.includes(node.key);
        return true;
      }
    }
    return false;
  }, [expandedKeys, preOrderTraversal]);
  const removeNode = key => {
    for (let node of preOrderTraversal()) {
      const filtered = node.children.filter(c => c.key !== key);
      if (filtered.length !== node.children.length) {
        node.children = filtered;
        return true;
      }
    }
    return false;
  };
  const findNode = useCallback(key => {
    for (let node of preOrderTraversal()) {
      if (node.key === key) return node;
    }
    return undefined;
  }, [preOrderTraversal]);

  // получаем список открытых элементов
  const getListExponded = useCallback(function* (node = rootRef.current) {
    node.expanded = expandedKeys.includes(node.key);

    // убираем рут элемент, так как он не нужен в массиве
    if (node.key !== -1) {
      if (node.key === treeData[0].key) {
        node.tabindex = 0;
      }
      yield node;
    }
    // рут элемент по умолчанию открыт
    if (node.hasChildren && (node.expanded || node.key === -1)) {
      for (let child of node.children) {
        yield* getListExponded(child);
      }
    }
  }, [rootRef, expandedKeys, treeData]);
  const convertToTreeData = useCallback((data, parentKey = -1, level = 0) => {
    data.forEach(element => {
      const {
        key,
        children,
        ...rest
      } = element;
      insertNode(parentKey, key, {
        ...rest,
        level
      });
      if (Array.isArray(children)) {
        convertToTreeData(children, key, level + 1);
      }
    });
    return [...getListExponded()];
  }, [getListExponded, insertNode]);
  const setDefaultChecked = useCallback(checkedKeys => {
    const checkedKeysSet = new Set(checkedKeys);
    checkedKeys.forEach(key => {
      const node = findNode(key);
      for (let child of postOrderTraversal(node)) {
        checkedKeysSet.add(child.key);
      }
    });
    setCheckedKeys([...checkedKeysSet.values()]);
  }, [findNode, postOrderTraversal]);
  const setFocusToItem = useCallback((item, direction = 'up') => {
    const nodeIdx = list.findIndex(node => node.key === item.key);
    const directionCount = Number(direction === 'down') * 2 - 1;
    const idx = nodeIdx + directionCount;
    if (direction === 'up' && idx < 0) return;
    if (direction === 'down' && idx > list.length - 1) return;
    const newList = list.map((node, index) => {
      if (idx === index) {
        node.tabindex = 0;
        node.ref.current?.focus();
        return node;
      }
      node.tabindex = -1;
      return node;
    });
    return setList(newList);
  }, [list, setList]);
  useEffect(() => {
    if (prevProps?.treeData !== treeData) {
      rootRef.current = new TreeNode(-1, {
        title: 'root',
        level: 0
      });
      setList(convertToTreeData(treeData));
    }
  }, [treeData, convertToTreeData, prevProps?.treeData]);
  useEffect(() => {
    if (defaultExpandedKeys.length && prevProps?.defaultExpandedKeys !== defaultExpandedKeys && rootRef.current) {
      setExpandedKeys(defaultExpandedKeys);
      setList([...getListExponded()]);
    }
  }, [defaultExpandedKeys, prevProps?.defaultExpandedKeys, getListExponded, rootRef]);
  useEffect(() => {
    if (checkable && checkable !== prevProps?.checkable && rootRef.current) {
      setDefaultChecked(defaultCheckedKeys);
    }
    if (!checkable && checkable !== prevProps?.checkable) {
      setCheckedKeys([]);
    }
  }, [setDefaultChecked, checkable, prevProps?.checkable, defaultCheckedKeys, rootRef]);
  useEffect(() => {
    setList([...getListExponded()]);
  }, [expandedKeys, getListExponded]);
  return {
    list,
    checkedKeys,
    expandedKeys,
    findNode,
    removeNode,
    preOrderTraversal,
    postOrderTraversal,
    getListExponded,
    setExpandedKeys,
    setCheckedKeys,
    setDefaultChecked,
    setList,
    setFocusToItem
  };
};
export default useTree;